/**
 * RLQ — shared lobby data + helpers.
 *
 * Defines the front-end-friendly `LobbyVisitor` view-model used across all
 * four lobby surfaces (Queue, Entry/Internal, Entry/External read-back, and
 * Management). The view-model deliberately strips out raw GUIDs/Cart IDs/
 * sessionIds/officer codes from what we *render* — those values are kept on
 * the object so click-handlers can navigate, but every visible label is the
 * humanized form.
 *
 * Per Bill 2026-04-23 brief:
 *   "Do not surface information that is not needed or usable for the user
 *    such as Cart ID, GUIDs, etc."
 *
 * Backend status (2026-04-24):
 *   - `/v3/sessions?status=active&top=N` exists and returns SessionDto rows.
 *     We filter client-side to `sessionType==='lobby' || origin==='kiosk'`
 *     until a dedicated `/v3/lobby/queue` route lands (planned).
 *   - There is no `/v3/lobby/entries` PUT/POST yet. The Internal entry form
 *     creates a session via `client.retail.sessions.create()` (same path the
 *     KioskCheckIn already uses) — that's our temporary entry endpoint.
 *   - No `/v3/lobby/{id}/assign` route — Management "Assign…" is a no-op
 *     today and surfaces a soft toast saying so. See follow-up note in the
 *     `LobbyManagement` component header.
 */

import type { SessionDto } from '@retail-ops/shared-types';
import type { Branch } from '@retail-ops/shared-types';

import {
  FALLBACK_SERVICE_TYPES,
  type LobbyEntryRow as LegacyLobbyEntryRow,
  type RequiredPermission,
  type ServiceTypeOption,
  type ServiceTypeTargetModule,
} from '../types';

// ---------------------------------------------------------------------------
// Visitor view-model
// ---------------------------------------------------------------------------

/** Channel a visitor arrived through. Drives the badge in the card footer. */
export type LobbyChannel = 'walk-in' | 'online' | 'kiosk' | 'phone' | 'appt';

/** Wait-time tier — drives the accent on the wait chip + card border. */
export type LobbyWaitTier = 'fresh' | 'normal' | 'warning' | 'breached';

export interface LobbyVisitor {
  /** Hidden internal id used by click handlers; never rendered. */
  readonly _sessionId: string;
  /** Hidden — used only when bootstrapping a cart on claim. */
  readonly _cartId?: string;
  /** Hidden — used only when bootstrapping a CIF on claim. */
  readonly _cifId?: string;

  /** Display name. "Walk-in #N" if nobody is identified yet. */
  displayName: string;
  /** 1–2 character avatar string (initials or `#`). */
  initials: string;
  /** Optional free-text hint shown under the name (never shows a GUID). */
  subtitle?: string;

  /** Plain-language reason for visit ("Open new account"). */
  reasonLabel: string;
  /** Raw service-type code — used for permission gating, not rendered. */
  serviceTypeCode?: string;
  /** Permission needed to claim — rendered as a small chip if not "Any". */
  requiredPermission: RequiredPermission;
  /** The module the claim should route into. */
  targetModule: ServiceTypeTargetModule;

  /** Officer display name if assigned, otherwise undefined. */
  assignedOfficerName?: string;
  /** Branch display name (resolved from Branch[] config). */
  branchName: string;

  /** Minutes elapsed since checkin. */
  waitMinutes: number;
  /** Bucket for color/severity. */
  waitTier: LobbyWaitTier;
  /** Pre-formatted relative wait string ("7 min"). */
  waitLabel: string;

  /** ISO timestamp the entry was created — used for sorting. */
  startedAt: string;
  /** Channel the visitor arrived through. */
  channel: LobbyChannel;
  /** Optional appointment time if the entry came from RAE. */
  scheduledAt?: string;
}

// ---------------------------------------------------------------------------
// Wait-time logic
// ---------------------------------------------------------------------------

/**
 * Wait-tier thresholds in minutes. Aligned with `LobbyEntryRow`'s existing
 * thresholds and RPM alert-config defaults so the lobby + supervisor metrics
 * agree at a glance.
 */
export const WAIT_TIER_THRESHOLDS = {
  freshMax: 5,
  normalMax: 15,
  warningMax: 25,
} as const;

export function classifyWait(waitMinutes: number): LobbyWaitTier {
  if (waitMinutes <= WAIT_TIER_THRESHOLDS.freshMax) return 'fresh';
  if (waitMinutes <= WAIT_TIER_THRESHOLDS.normalMax) return 'normal';
  if (waitMinutes <= WAIT_TIER_THRESHOLDS.warningMax) return 'warning';
  return 'breached';
}

export function minutesSince(iso: string | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

export function waitLabelFor(waitMinutes: number): string {
  if (waitMinutes < 1) return 'Just arrived';
  if (waitMinutes < 60) return `${waitMinutes} min`;
  const h = Math.floor(waitMinutes / 60);
  const m = waitMinutes % 60;
  return m === 0 ? `${h} hr` : `${h}h ${m}m`;
}

// ---------------------------------------------------------------------------
// Initials + display-name helpers
// ---------------------------------------------------------------------------

const FALLBACK_NAMES = [
  'Walk-in guest',
  'Lobby guest',
  'New visitor',
] as const;

export function makeInitials(name: string | undefined, fallback = '?'): string {
  if (!name || !name.trim()) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function pickFallbackName(seed: number): string {
  const i = Math.abs(seed) % FALLBACK_NAMES.length;
  return FALLBACK_NAMES[i]!;
}

// ---------------------------------------------------------------------------
// Adapters: SessionDto → LobbyVisitor
// ---------------------------------------------------------------------------

export interface AdaptOptions {
  serviceMap: ReadonlyMap<string, ServiceTypeOption>;
  branchMap: ReadonlyMap<string, Branch>;
  /** Position in the queue when the visitor is anonymous (1-based). */
  walkInIndex?: number;
}

export function buildServiceMap(
  options: ReadonlyArray<ServiceTypeOption> = FALLBACK_SERVICE_TYPES,
): Map<string, ServiceTypeOption> {
  return new Map(options.map((o) => [o.code, o]));
}

export function buildBranchMap(branches: ReadonlyArray<Branch>): Map<string, Branch> {
  return new Map(branches.map((b) => [b.branchCode, b]));
}

function reasonFor(svc: ServiceTypeOption | undefined, raw: string | undefined): string {
  if (svc?.label) return svc.label;
  if (raw && raw.trim()) {
    // Title-case kebab/camel codes for a humane fallback.
    return raw
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'General lobby';
}

function channelFor(s: SessionDto): LobbyChannel {
  if (s.origin === 'kiosk') return 'kiosk';
  if (s.origin === 'online') return 'online';
  if (s.origin === 'phone') return 'phone';
  if (s.origin === 'in-branch' || s.origin === 'fsr-inbranch') return 'walk-in';
  return 'walk-in';
}

export function sessionToVisitor(s: SessionDto, opts: AdaptOptions): LobbyVisitor {
  const svc = s.serviceType ? opts.serviceMap.get(s.serviceType) : undefined;
  const reasonLabel = reasonFor(svc, s.serviceType);
  const branch = opts.branchMap.get(s.branch);
  const branchName = branch?.branchName ?? `Branch ${s.branch || '—'}`;
  const wait = minutesSince(s.startedAt);

  // Identification — never expose the cifId raw in the UI; if we have a
  // cifId we render "Returning customer" until a name lookup is wired.
  // Officer-name is suppressed when the session is parked on the KIOSK
  // pseudo-officer (means: nobody has claimed yet).
  const hasRealOfficer = !!s.officer && s.officerCode !== 'KIOSK';

  // Build a display name. Prefer officerName if it's actually the *visitor*
  // we already captured at kiosk — but the v1 shape doesn't carry it; for
  // now we surface the cifId-presence as "Returning customer", or a fallback.
  let displayName: string;
  let subtitle: string | undefined;
  if ((s as unknown as { customerName?: string }).customerName) {
    displayName = (s as unknown as { customerName: string }).customerName;
  } else if (s.cifId) {
    displayName = 'Returning customer';
    subtitle = 'Identified at check-in';
  } else if (typeof opts.walkInIndex === 'number') {
    displayName = `Walk-in #${opts.walkInIndex}`;
  } else {
    displayName = pickFallbackName(s.startedAt.charCodeAt(0));
  }

  return {
    _sessionId: s.sessionId,
    ...(s.cartId !== undefined ? { _cartId: s.cartId } : {}),
    ...(s.cifId !== undefined ? { _cifId: s.cifId } : {}),
    displayName,
    initials: makeInitials(displayName, '#'),
    ...(subtitle !== undefined ? { subtitle } : {}),
    reasonLabel,
    ...(s.serviceType !== undefined ? { serviceTypeCode: s.serviceType } : {}),
    requiredPermission: svc?.requiredPermission ?? 'Any',
    targetModule: svc?.targetModule ?? 'RCC',
    ...(hasRealOfficer && s.officerName
      ? { assignedOfficerName: s.officerName }
      : hasRealOfficer
        ? { assignedOfficerName: 'Banker (claimed)' }
        : {}),
    branchName,
    waitMinutes: wait,
    waitTier: classifyWait(wait),
    waitLabel: waitLabelFor(wait),
    startedAt: s.startedAt,
    channel: channelFor(s),
  };
}

// ---------------------------------------------------------------------------
// Legacy adapter — for code paths still on the LegacyLobbyEntryRow shape.
// ---------------------------------------------------------------------------

export function legacyEntryToVisitor(
  e: LegacyLobbyEntryRow,
  opts: AdaptOptions,
): LobbyVisitor {
  const svc = e.serviceTypeCode ? opts.serviceMap.get(e.serviceTypeCode) : undefined;
  const branch = e.branch ? opts.branchMap.get(e.branch) : undefined;
  const wait = e.waitMinutes;
  const displayName =
    e.customerName && !e.customerName.startsWith('CIF ')
      ? e.customerName
      : e.cifId
        ? 'Returning customer'
        : `Walk-in (${(opts.walkInIndex ?? 1)})`;
  return {
    _sessionId: e.sessionId,
    ...(e.cartId !== undefined ? { _cartId: e.cartId } : {}),
    ...(e.cifId !== undefined ? { _cifId: e.cifId } : {}),
    displayName,
    initials: makeInitials(displayName, '#'),
    ...(e.customerHint !== undefined && !e.customerHint.toLowerCase().includes('cif')
      ? { subtitle: e.customerHint }
      : {}),
    reasonLabel: e.serviceTypeLabel ?? svc?.label ?? 'General lobby',
    ...(e.serviceTypeCode !== undefined ? { serviceTypeCode: e.serviceTypeCode } : {}),
    requiredPermission: e.requiredPermission ?? svc?.requiredPermission ?? 'Any',
    targetModule: e.targetModule ?? svc?.targetModule ?? 'RCC',
    ...(e.fsrName ? { assignedOfficerName: e.fsrName } : {}),
    branchName: branch?.branchName ?? (e.branch ? `Branch ${e.branch}` : 'PCB Branch'),
    waitMinutes: wait,
    waitTier: classifyWait(wait),
    waitLabel: waitLabelFor(wait),
    startedAt: e.startedAt,
    channel: 'walk-in',
  };
}

// ---------------------------------------------------------------------------
// Permission gate
// ---------------------------------------------------------------------------

/** Roles that may see the Management surface. */
export const MANAGEMENT_ROLES = new Set([
  'Supervisor',
  'Manager',
  'Executive',
  'Admin',
  'SuperAdmin',
]);

/** Roles that may see the External entry surface (mostly admin/dev). */
export const EXTERNAL_ENTRY_ROLES = new Set([
  'Manager',
  'Executive',
  'Admin',
  'SuperAdmin',
]);

/** Roles permitted to operate on any lobby entry as staff. */
export const STAFF_ROLES = new Set([
  'Officer',
  'Supervisor',
  'Manager',
  'Executive',
  'Admin',
  'SuperAdmin',
]);

export function canSeeManagement(primaryRole: string | undefined): boolean {
  return !!primaryRole && MANAGEMENT_ROLES.has(primaryRole);
}

export function canSeeExternalEntry(primaryRole: string | undefined): boolean {
  return !!primaryRole && EXTERNAL_ENTRY_ROLES.has(primaryRole);
}

export function isKioskRole(_primaryRole: string | undefined, isKioskFlag?: boolean): boolean {
  // Kiosk is no longer a user role (it's an Azure SPA + isKioskFlag claim).
  // The first arg is retained for call-site back-compat but is intentionally
  // unused now that Kiosk has been removed from the UserRole union.
  return isKioskFlag === true;
}

// ---------------------------------------------------------------------------
// Sort order: warning/breached first, oldest first within tier.
// ---------------------------------------------------------------------------

const TIER_ORDER: Record<LobbyWaitTier, number> = {
  breached: 0,
  warning: 1,
  normal: 2,
  fresh: 3,
};

export function sortLobbyVisitors(
  visitors: ReadonlyArray<LobbyVisitor>,
): ReadonlyArray<LobbyVisitor> {
  return visitors.slice().sort((a, b) => {
    const tierDelta = TIER_ORDER[a.waitTier] - TIER_ORDER[b.waitTier];
    if (tierDelta !== 0) return tierDelta;
    return b.waitMinutes - a.waitMinutes;
  });
}

// ---------------------------------------------------------------------------
// WAVE-9 — LobbyTicket → LobbyVisitor adapter
// ---------------------------------------------------------------------------
// The new `/v3/lobby/queue` route returns LobbyTicket rows backed by
// dbo.LobbyQueueTickets. This adapter brings them into the existing
// LobbyVisitor view-model so the Queue/Management surfaces can switch their
// data source without rewriting card/list rendering.
// ---------------------------------------------------------------------------

import type { LobbyTicket } from '@retail-ops/shared-types';

function channelForTicket(t: LobbyTicket): LobbyChannel {
  switch (t.entryChannel) {
    case 'kiosk': return 'kiosk';
    case 'phone': return 'phone';
    case 'rcp-portal-pre-arrive': return 'online';
    case 'bookings-check-in': return 'appt';
    default: return 'walk-in';
  }
}

export interface AdaptTicketOptions {
  serviceMap: ReadonlyMap<string, ServiceTypeOption>;
  branchMap: ReadonlyMap<string, Branch>;
  walkInIndex?: number;
}

export function lobbyTicketToVisitor(
  t: LobbyTicket,
  opts: AdaptTicketOptions,
): LobbyVisitor {
  const svc = t.reasonCode ? opts.serviceMap.get(t.reasonCode) : undefined;
  const reasonLabel = svc?.label ?? t.reasonDescription ?? reasonFor(svc, t.reasonCode);
  const branch = opts.branchMap.get(t.branchCode);
  const branchName = branch?.branchName ?? `Branch ${t.branchCode || '—'}`;
  const wait = typeof t.waitMinutes === 'number'
    ? t.waitMinutes
    : minutesSince(t.arrivedAt);

  let displayName: string;
  let subtitle: string | undefined;
  if (t.customerName && t.customerName.trim()) {
    displayName = t.customerName;
  } else if (t.customerCif) {
    displayName = 'Returning customer';
    subtitle = 'Identified at check-in';
  } else if (typeof opts.walkInIndex === 'number') {
    displayName = `Walk-in #${opts.walkInIndex}`;
  } else {
    displayName = pickFallbackName(t.arrivedAt.charCodeAt(0));
  }

  return {
    _sessionId: t.ticketId,
    ...(t.cartId !== undefined ? { _cartId: t.cartId } : {}),
    ...(t.customerCif !== undefined ? { _cifId: t.customerCif } : {}),
    displayName,
    initials: makeInitials(displayName, '#'),
    ...(subtitle !== undefined ? { subtitle } : {}),
    reasonLabel,
    ...(t.reasonCode !== undefined ? { serviceTypeCode: t.reasonCode } : {}),
    requiredPermission: svc?.requiredPermission ?? 'Any',
    targetModule: svc?.targetModule ?? 'RCC',
    ...(t.claimedByOfficerCode
      ? { assignedOfficerName: `Officer ${t.claimedByOfficerCode}` }
      : {}),
    branchName,
    waitMinutes: wait,
    waitTier: classifyWait(wait),
    waitLabel: waitLabelFor(wait),
    startedAt: t.arrivedAt,
    channel: channelForTicket(t),
  };
}

/**
 * Feature flag — when true, RLQ surfaces fetch from /v3/lobby/queue (the
 * Wave-9 backend) instead of /v3/sessions. Honors `?lobbyV9=1` query param
 * or `VITE_FEATURE_LOBBY_V9=true` env. Defaults true so the new
 * Wave-9 path is on whenever the route exists.
 */
export function isLobbyV9Enabled(): boolean {
  if (typeof window !== 'undefined') {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('lobbyV9') === '0') return false;
    if (sp.get('lobbyV9') === '1') return true;
  }
  const env = (import.meta as unknown as { env?: { VITE_FEATURE_LOBBY_V9?: string } }).env;
  if (env?.VITE_FEATURE_LOBBY_V9 === 'false') return false;
  return true;
}
